import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { firestore } from "./firebase";
// --- MUI Imports ---
import {
  Paper,
  Typography,
  IconButton,
  Box,
  Grid,
  Card,
} from "@mui/material";
// --- End MUI Imports ---
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getScheduleDueStats, toDate } from "./utils/amcScheduleStats";

const HomePage = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(() => sessionStorage.getItem("userRole"));

  // State for Admin/Customer
  const [warrantyCount, setWarrantyCount] = useState(0);
  const [warrantyDueNextMonth, setWarrantyDueNextMonth] = useState(0);
  const [warrantyDueToday, setWarrantyDueToday] = useState(0);
  const [amcCount, setAmcCount] = useState(0);
  const [amcDueNextMonth, setAmcDueNextMonth] = useState(0);
  const [amcDueToday, setAmcDueToday] = useState(0);

  // ✅ --- New state for Contracts ---
  const [contractCount, setContractCount] = useState(0);
  const [contractDueNextMonth, setContractDueNextMonth] = useState(0);
  const [contractDueToday, setContractDueToday] = useState(0);

  // ✅ --- New state for Membership ---
  const [membershipTotal, setMembershipTotal] = useState(0);
  const [membershipPending, setMembershipPending] = useState(0);
  const [membershipActive, setMembershipActive] = useState(0);

  // ✅ --- New state for Facility ---
  const [facilityTotal, setFacilityTotal] = useState(0);
  const [facilityActive, setFacilityActive] = useState(0);
  const [tenantName, setTenantName] = useState(
    () => sessionStorage.getItem("tenantName") || ""
  );
  const [isResolvingTenantName, setIsResolvingTenantName] = useState(false);


  // State for Seller stats
  const [shopStats, setShopStats] = useState({
    totalShops: 0,
    activeShops: 0,
    totalComplaints: 0,
  });

  const auth = getAuth();
  const isFacilityOperatorRole = (role) =>
    ["seller", "facility_operator"].includes(String(role || "").trim().toLowerCase());
  const isTechnicianRole = (role) =>
    String(role || "").trim().toLowerCase() === "technician";

  useEffect(() => {
    if (["organisation_admin", "OU Admin"].includes(userRole)) {
      navigate("/organisation-admin");
    }
    if (userRole === "facility_admin") {
      navigate("/facility-admin");
    }
    if (userRole === "OU Operator") {
      navigate("/ou-operator");
    }
    if (["OU User", "organisation_operator"].includes(userRole)) {
      navigate("/ou-user");
    }
    if (isTechnicianRole(userRole)) {
      navigate("/technician");
    }
  }, [userRole, navigate]);

  const resolveTenantName = useCallback(async (tenantId, userEmail) => {
    try {
      const normalizedEmail = String(userEmail || "").trim().toLowerCase();

      if (tenantId) {
        const tenantByDocId = await getDoc(doc(firestore, "TenantMaster", tenantId));
        if (tenantByDocId.exists()) {
          return String(tenantByDocId.data()?.tenantName || "").trim();
        }

        const tenantByFieldQuery = query(
          collection(firestore, "TenantMaster"),
          where("tenantId", "==", tenantId)
        );
        const tenantByFieldSnapshot = await getDocs(tenantByFieldQuery);
        if (!tenantByFieldSnapshot.empty) {
          return String(tenantByFieldSnapshot.docs[0].data()?.tenantName || "").trim();
        }
      }

      if (normalizedEmail) {
        const tenantByEmailQuery = query(
          collection(firestore, "TenantMaster"),
          where("adminEmail", "==", normalizedEmail)
        );
        const tenantByEmailSnapshot = await getDocs(tenantByEmailQuery);
        if (!tenantByEmailSnapshot.empty) {
          return String(tenantByEmailSnapshot.docs[0].data()?.tenantName || "").trim();
        }
      }

      return "";
    } catch (error) {
      console.error("Error resolving tenant name:", error);
      return "";
    }
  }, []);

  // --- All your data fetching logic (UNCHANGED) ---
  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const endOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  const oneMonthFromNow = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const subscribeWarrantyCount = useCallback((userId) => {
    const q = query(
      collection(firestore, "ItemMaster"),
      where("userId", "==", userId)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setWarrantyCount(snapshot.size);
      let dueNextMonth = 0,
        dueToday = 0;
      snapshot.forEach((doc) => {
        const expiry = toDate(doc.data().warrantyExpiryDate);
        if (!expiry) return;
        if (expiry >= startOfToday && expiry <= endOfToday) dueToday++;
        if (expiry > endOfToday && expiry <= oneMonthFromNow) dueNextMonth++;
      });
      setWarrantyDueNextMonth(dueNextMonth);
      setWarrantyDueToday(dueToday);
    });
    return unsubscribe;
  }, [startOfToday, endOfToday, oneMonthFromNow]);


  const normalizeEmail = (email) =>
    String(email || "").trim().toLowerCase();



  const subscribeAmcCount = useCallback(
    (userEmail, userRole, tenantId) => {
      const normalizedEmail = normalizeEmail(userEmail);
      let q;

      if (userRole === "admin") {
        q = tenantId
          ? query(
              collection(firestore, "AMCScheduleMaster"),
              where("tenantId", "==", tenantId)
            )
          : query(collection(firestore, "AMCScheduleMaster"));
      } else {
        const constraints = [
          where("createdBy", "==", normalizedEmail),
        ];
        if (tenantId) {
          constraints.push(where("tenantId", "==", tenantId));
        }
        q = query(collection(firestore, "AMCScheduleMaster"), ...constraints);
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          setAmcCount(snapshot.size);
          const { dueToday, dueNextMonth } = getScheduleDueStats(
            snapshot.docs.map((entry) => entry.data()),
            startOfToday,
            endOfToday,
            oneMonthFromNow
          );

          setAmcDueNextMonth(dueNextMonth);
          setAmcDueToday(dueToday);
        },
        (error) => {
          console.error("Error subscribing AMC count:", error);
        }
      );

      return unsubscribe;
    },
    [startOfToday, endOfToday, oneMonthFromNow]
  );

  // ✅ --- New function for Contracts ---
  const subscribeContractCount = useCallback((role, userId, tenantId) => {
    const normalizedRole = String(role || "").trim().toLowerCase();
    const normalizedTenantId = String(tenantId || "").trim();

    if (normalizedRole === "admin" && !normalizedTenantId) {
      setContractCount(0);
      setContractDueNextMonth(0);
      setContractDueToday(0);
      return () => {};
    }

    const q =
      normalizedRole === "admin"
        ? query(
            collection(firestore, "ContractMaster"),
            where("tenantId", "==", normalizedTenantId)
          )
        : query(
            collection(firestore, "ContractMaster"),
            where("createdById", "==", userId)
          );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let activeCount = 0;
      let dueNextMonth = 0;
      let dueToday = 0;

      snapshot.forEach((entry) => {
        const data = entry.data() || {};
        const status = String(data.status || "").trim().toLowerCase();
        if (status !== "active") return;

        activeCount += 1;
        const expiry = toDate(data.endDate);
        if (!expiry) return;
        if (expiry >= startOfToday && expiry <= endOfToday) dueToday++;
        if (expiry > endOfToday && expiry <= oneMonthFromNow) dueNextMonth++;
      });

      setContractCount(activeCount);
      setContractDueNextMonth(dueNextMonth);
      setContractDueToday(dueToday);
    });
    return unsubscribe;
  }, [startOfToday, endOfToday, oneMonthFromNow]);

  // ✅ --- New subscription function for Seller Shop Stats ---
  const subscribeSellerShopStats = useCallback((user) => {
    try {
      const normalizedEmail = String(user?.email || "").trim();
      const listeners = [];

      const updateShopStats = (snapshot) => {
        let activeShopCount = 0;

        snapshot.forEach((entry) => {
          const data = entry.data() || {};
          const status = String(data.status || "Active").toLowerCase();
          const isMarkedInactive = data.isActive === false || status === "inactive";
          if (!isMarkedInactive) {
            activeShopCount += 1;
          }
        });

        setShopStats((prev) => ({
          ...prev,
          totalShops: snapshot.size,
          activeShops: activeShopCount,
        }));
      };

      const complaintBuckets = {
        assignedSellerEmail: new Map(),
        sellerEmail: new Map(),
        shopEmail: new Map(),
        userId: new Map(),
      };

      const isNonResolvedComplaint = (statusValue) =>
        String(statusValue || "")
          .trim()
          .toLowerCase() !== "resolved";

      const updateComplaintCount = () => {
        const mergedComplaintStatusMap = new Map([
          ...complaintBuckets.assignedSellerEmail,
          ...complaintBuckets.sellerEmail,
          ...complaintBuckets.shopEmail,
          ...complaintBuckets.userId,
        ]);

        const nonResolvedCount = Array.from(mergedComplaintStatusMap.values()).filter(
          isNonResolvedComplaint
        ).length;

        setShopStats((prev) => ({
          ...prev,
          totalComplaints: nonResolvedCount,
        }));
      };

      if (normalizedEmail) {
        const shopsQuery = query(
          collection(firestore, "shops"),
          where("email", "==", normalizedEmail)
        );

        listeners.push(
          onSnapshot(
            shopsQuery,
            updateShopStats,
            (error) => console.error("Error subscribing to seller shops:", error)
          )
        );

        const complaintFields = ["assignedSellerEmail", "sellerEmail", "shopEmail"];

        complaintFields.forEach((fieldName) => {
          const complaintsByEmailQuery = query(
            collection(firestore, "Complaints"),
            where(fieldName, "==", normalizedEmail)
          );

          listeners.push(
            onSnapshot(
              complaintsByEmailQuery,
              (snapshot) => {
                complaintBuckets[fieldName] = new Map(
                  snapshot.docs.map((entry) => [entry.id, entry.data()?.status || ""])
                );
                updateComplaintCount();
              },
              (error) => console.error(`Error subscribing complaints by ${fieldName}:`, error)
            )
          );
        });
      }

      if (user?.uid) {
        const complaintsByUserQuery = query(
          collection(firestore, "Complaints"),
          where("userId", "==", user.uid)
        );

        listeners.push(
          onSnapshot(
            complaintsByUserQuery,
            (snapshot) => {
              complaintBuckets.userId = new Map(
                snapshot.docs.map((entry) => [entry.id, entry.data()?.status || ""])
              );
              updateComplaintCount();
            },
            (error) => console.error("Error subscribing complaints by userId:", error)
          )
        );
      }

      return () => {
        listeners.forEach((unsubscribe) => unsubscribe());
      };
    } catch (error) {
      console.error("Error subscribing to seller shop stats:", error);
      return () => {};
    }
  }, []);

  // ✅ --- New subscription function for Membership Stats ---
  const subscribeMembershipStats = useCallback((role, userEmail, tenantId) => {
    try {
      const normalizedRole = String(role || "").toLowerCase();
      const email = String(userEmail || "").trim();
      const isAdminView = normalizedRole === "admin";
      const listeners = [];
      const counters = {
        total: 0,
        pending: 0,
        active: 0,
      };

      // Keep argument to preserve existing call signature.
      void tenantId;

      const publishCounters = () => {
        setMembershipTotal(counters.total);
        setMembershipPending(counters.pending);
        setMembershipActive(counters.active);
      };

      const masterQuery = isAdminView
        ? query(collection(firestore, "MembershipMaster"))
        : query(collection(firestore, "MembershipMaster"), where("personalEmail", "==", email));

      listeners.push(
        onSnapshot(
          masterQuery,
          (snapshot) => {
            let total = 0;
            let active = 0;

            snapshot.forEach((entry) => {
              const status = String(entry.data()?.status || "").toLowerCase();
              if (status === "rejected") {
                return;
              }

              total += 1;
              if (status === "active" || status === "approved") {
                active += 1;
              }
            });

            counters.total = total;
            counters.active = active;
            publishCounters();
          },
          (error) => {
            console.error("Error subscribing membership master stats:", error);
          }
        )
      );

      const pendingQuery = isAdminView
        ? query(collection(firestore, "MembershipRequests"), where("status", "==", "pending"))
        : query(
            collection(firestore, "MembershipRequests"),
            where("personalEmail", "==", email),
            where("status", "==", "pending")
          );

      listeners.push(
        onSnapshot(
          pendingQuery,
          (snapshot) => {
            counters.pending = snapshot.size;
            publishCounters();
          },
          (error) => {
            console.error("Error subscribing membership pending stats:", error);
          }
        )
      );

      return () => {
        listeners.forEach((unsubscribe) => unsubscribe());
      };
    } catch (error) {
      console.error("Error subscribing to membership stats:", error);
      return () => {};
    }
  }, []);

  const subscribeFacilityStats = useCallback((tenantId) => {
    try {
      if (!tenantId) {
        setFacilityTotal(0);
        setFacilityActive(0);
        return () => {};
      }

      const facilityQuery = query(
        collection(firestore, "UserProfileMaster"),
        where("tenantId", "==", tenantId),
        where("role", "==", "facility_admin")
      );

      const unsubscribe = onSnapshot(facilityQuery, (snapshot) => {
        const total = snapshot.size;
        let active = 0;

        snapshot.forEach((entry) => {
          const status = String(entry.data()?.status || "Active").toLowerCase();
          if (status === "active") active++;
        });

        setFacilityTotal(total);
        setFacilityActive(active);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error subscribing facility stats:", error);
      setFacilityTotal(0);
      setFacilityActive(0);
      return () => {};
    }
  }, []);

  // ✅ --- Updated useEffect ---
  useEffect(() => {
    const roleFromSession = sessionStorage.getItem("userRole");
    if (roleFromSession) setUserRole(roleFromSession);
    const cachedTenantName = sessionStorage.getItem("tenantName") || "";
    if (cachedTenantName) {
      setTenantName(cachedTenantName);
    }

    let unsubscribeWarranty = () => {};
    let unsubscribeContracts = () => {};
    let unsubscribeAmc = () => {};
    let unsubscribeSellerStats = () => {};
    let unsubscribeMembership = () => {};
    let unsubscribeFacility = () => {};

    let disposed = false;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        let finalRole = roleFromSession;
        let tenantIdForAdmin = sessionStorage.getItem("tenantId") || "";

        if (!finalRole) {
          const q = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", user.email)
          );
          const snap = await getDocs(q);
          finalRole = snap.empty
            ? "customer"
            : snap.docs[0].data().role || "customer";
          tenantIdForAdmin = snap.empty
            ? tenantIdForAdmin
            : (snap.docs[0].data().tenantId || snap.docs[0].data().tenantid || tenantIdForAdmin);
          setUserRole(finalRole);
          sessionStorage.setItem("userRole", finalRole);
        }

        if (finalRole === "admin" && !tenantIdForAdmin && user.email) {
          const tenantQuery = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", user.email)
          );
          const tenantSnapshot = await getDocs(tenantQuery);
          if (!tenantSnapshot.empty) {
            const profileData = tenantSnapshot.docs[0].data() || {};
            tenantIdForAdmin =
              profileData.tenantId || profileData.tenantid || tenantIdForAdmin;
          }
        }

        if (finalRole === "admin") {
          setIsResolvingTenantName(true);
          const resolvedName = await resolveTenantName(tenantIdForAdmin, user.email);
          if (disposed) return;
          setTenantName(resolvedName);
          if (resolvedName) {
            sessionStorage.setItem("tenantName", resolvedName);
          } else {
            sessionStorage.removeItem("tenantName");
          }
          setIsResolvingTenantName(false);
        } else {
          setTenantName("");
          setIsResolvingTenantName(false);
          sessionStorage.removeItem("tenantName");
        }

        if (isFacilityOperatorRole(finalRole)) {
          unsubscribeSellerStats = subscribeSellerShopStats(user);

          unsubscribeAmc = subscribeAmcCount(finalRole, user.email, tenantIdForAdmin);
        } else if (["admin", "customer"].includes(finalRole)) {
          unsubscribeWarranty = subscribeWarrantyCount(user.uid);
          unsubscribeAmc = subscribeAmcCount(finalRole, user.email, tenantIdForAdmin);
          unsubscribeContracts = subscribeContractCount(finalRole, user.uid, tenantIdForAdmin);
          unsubscribeMembership = subscribeMembershipStats(finalRole, user.email, tenantIdForAdmin);
          if (finalRole === "admin") {
            unsubscribeFacility = subscribeFacilityStats(
              tenantIdForAdmin
            );
          }
        }

        return () => {
          // ✅ Update cleanup - call all unsubscribe functions
          unsubscribeWarranty();
          unsubscribeContracts();
          unsubscribeAmc();
          unsubscribeSellerStats();
          unsubscribeMembership();
          unsubscribeFacility();
        };
      }
    });

    return () => {
      disposed = true;
      unsubscribeAuth();
    };
  }, [auth, resolveTenantName, subscribeSellerShopStats, subscribeAmcCount, subscribeContractCount, subscribeMembershipStats, subscribeWarrantyCount, subscribeFacilityStats]);
  // --- End of Updated Logic ---

  // --- Re-usable Action Card Component ---
  const ActionCard = ({ title, text, navigateTo }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "12px",
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {text}
        </Typography>
      </Box>
      <IconButton color="primary" onClick={() => navigate(navigateTo)}>
        <ArrowForwardIosIcon />
      </IconButton>
    </Paper>
  );

  // --- Super Admin View (UNCHANGED) ---
  const isSuperAdmin =
    userRole === "superadmin" ||
    sessionStorage.getItem("userRole") === "superadmin";

  if (isSuperAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          textAlign: "center",
          p: 3,
        }}
      >
        <Box
          component="img"
          src={require("./OV-WarrantyMS.png")}
          alt="Logo"
          sx={{ maxWidth: "300px", width: "100%", mb: 4 }}
        />
        <Typography
          variant="h3"
          component="h1"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          WELCOME TO AALAY
        </Typography>
        <Typography variant="h5" sx={{ color: "text.secondary", mt: 2 }}>
          You are logged in as Super Admin
        </Typography>
      </Box>
    );
  }

  // --- Seller View (UNCHANGED) ---
  if (isFacilityOperatorRole(userRole)) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {" "}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Facility Operator Dashboard
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              {" "}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="h2">
                  Complaint Management
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Total Complaints"
                    value={shopStats.totalComplaints}
                    color="#D32F2F"
                  />
                </Grid>
              </Grid>
              <Box
                sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <ActionCard
                  title="Complaints"
                  text="View complaints related to your business vendors."
                  navigateTo="/complaints_list"
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (isTechnicianRole(userRole)) {
    const storedProfile = (() => {
      try {
        return JSON.parse(sessionStorage.getItem("userProfile") || "{}");
      } catch {
        return {};
      }
    })();
    const displayName =
      sessionStorage.getItem("userDisplayName") ||
      storedProfile.displayName ||
      storedProfile.adminName ||
      sessionStorage.getItem("userEmail") ||
      "Technician";

    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Technician Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          Welcome, {displayName}
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                Maintenance Work
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <ActionCard
                  title="Assigned Maintenance Schedules"
                  text="View AMC schedules assigned to you."
                  navigateTo="/amc/schedule/list"
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  

  // --- Default Admin & Customer View (MODIFIED FOR EQUAL HEIGHT) ---
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {" "}
      {userRole === "admin" && (tenantName || isResolvingTenantName) && (
        <Typography
          variant="h6"
          component="h2"
          sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5 }}
        >
          {tenantName || "Loading tenant..."}
        </Typography>
      )}
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        Welcome to Aalay
      </Typography>
      {["admin", "customer"].includes(userRole) && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* ✅ --- CARD 1: Warranty --- */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                height: "100%",
                display: "flex", // <-- ADDED
                flexDirection: "column", // <-- ADDED
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Warranty
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Total available"
                    value={warrantyCount}
                    color="#1976d2"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Expire in next month"
                    value={warrantyDueNextMonth}
                    color="#EF6C00"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Targeted for today"
                    value={warrantyDueToday}
                    color="#D32F2F"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                {userRole === "admin"
                  ? "Warranty Admin Panel"
                  : "My Warranty & Complaints"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flexGrow: 1, // <-- ADDED
                }}
              >
                <ActionCard
                  title="Warranty Cards"
                  text="View and manage your warranty items."
                  navigateTo="/warranty_list"
                />
                <ActionCard
                  title="Complaints"
                  text="View or raise complaints for your products."
                  navigateTo="/complaints_list"
                />
              </Box>
            </Card>
          </Grid>

          {/* ✅ --- CARD 2: AMC --- */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                height: "100%",
                display: "flex", // <-- ADDED
                flexDirection: "column", // <-- ADDED
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600 }}
                >
                  Maintenance Schedules
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Total available"
                    value={amcCount}
                    color="#1976d2"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Expire in next month"
                    value={amcDueNextMonth}
                    color="#EF6C00"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Targeted for today"
                    value={amcDueToday}
                    color="#D32F2F"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                Maintenance Schedule Management
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flexGrow: 1, // <-- ADDED
                }}
              >
                <ActionCard
                  title="Create Maintenance Schedule"
                  text="Set up a new Annual Maintenance Contract."
                  navigateTo="/amc/schedule/new"
                />
                <ActionCard
                  title="Manage Maintenance Schedules"
                  text="View, update, or track maintenance schedules."
                  navigateTo="/amc/schedule/list"
                />
              </Box>
            </Card>
          </Grid>

          {/* ✅ --- CARD 3: Contracts --- */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                height: "100%",
                display: "flex", // <-- ADDED
                flexDirection: "column", // <-- ADDED
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600 }}
                >
                  Contracts
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Total active"
                    value={contractCount}
                    color="#1976d2"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Expire in next month"
                    value={contractDueNextMonth}
                    color="#EF6C00"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Ending today"
                    value={contractDueToday}
                    color="#D32F2F"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                Contract Management
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flexGrow: 1, // <-- ADDED
                }}
              >
                {userRole === "admin" && (
                  <ActionCard
                    title="Create New Contract"
                    text="Set up a new legal contract."
                    navigateTo="/contracts/new"
                  />
                )}
                <ActionCard
                  title="Manage Contracts"
                  text="View, update, or amend contracts."
                  navigateTo="/contracts/list"
                />
              </Box>
            </Card>
          </Grid>
          {/* ✅ --- END Contract Section Card --- ✅ */}

          {userRole === "admin" && (
            <Grid item xs={12} sm={6} lg={4}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: 600 }}
                  >
                    Facility
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Odometer
                      label="Total Facilities"
                      value={facilityTotal}
                      color="#1976d2"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Odometer
                      label="Active Facilities"
                      value={facilityActive}
                      color="#EF6C00"
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                  Facility Management
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flexGrow: 1,
                  }}
                >
                  <ActionCard
                    title="Manage Facility"
                    text="Create and manage Facility Admin access."
                    navigateTo="/manage-facility"
                  />
                  <ActionCard
                    title="Assigned AMC Schedules"
                    text="View AMC schedules assigned to facility admins."
                    navigateTo="/amc/schedule/list?assignedOnly=true"
                  />
                </Box>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600 }}
                >
                  Membership
                </Typography>
              </Box>

              {/* ✅ ODOMETER SECTION (ADD HERE) */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Total Members"
                    value={membershipTotal}
                    color="#1976d2"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Pending Approval"
                    value={membershipPending}
                    color="#EF6C00"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer
                    label="Active Members"
                    value={membershipActive}
                    color="#388E3C"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                Membership Management
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flexGrow: 1,
                }}
              >
              {userRole === "admin" && (
                <ActionCard
                  title="Approve Membership"
                  text="Review and approve membership requests."
                  navigateTo="/membership"
                />
                )}
                {userRole === "admin" && (
                <ActionCard
                  title="Membership Form"
                  text="Create or register a new membership."
                  navigateTo="/membership-form"
                />
                )}
                {userRole === "customer" && (
                <ActionCard
                  title="Digital Membership Card"
                  text="Manage and view your digital membership card."
                  navigateTo="/membership/card"
                />
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

          
    </Box>
    
  );
};


   



// --- Odometer Component (UNCHANGED) ---
const Odometer = ({ label, value, color }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const prevValueRef = useRef(0);

  useEffect(() => {
    let raf;
    const duration = 600;
    const start = performance.now();
    const initial = prevValueRef.current;
    const delta = value - initial;
    const animate = (t) => {
      const elapsed = t - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(initial + delta * eased);
      setDisplayValue(current);
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    prevValueRef.current = value;
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        height: "120px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary", m: 0, fontSize: "0.875rem" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, m: 0, color: color, mt: 1 }}>
        {displayValue}
      </Typography>
    </Paper>
  );
};

export default HomePage;
